// ChatComponent for RockkPay (User)
import React, { useEffect, useRef, useState } from "react";
import {
  connectWebSocket,
  disconnectWebSocket,
  sendMessage,
} from "./websocketService";
import { useParams } from "react-router-dom";
import {
  Avatar,
  Box,
  Button,
  Container,
  Divider,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";

const ChatComponent = () => {
  const { ticketId } = useParams();
  const [messages, setMessages] = useState([]);
  const [message, setMessage] = useState("");
  const [userid, setUserid] = useState("");
  const [isEmployeeConnected, setIsEmployeeConnected] = useState(false);
  const API_BASE_URL = "https://apisupport.ipaisa.co.in/supportapp";
  const messagesEndRef = useRef(null);

  const fetchChatHistory = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/chatsession/messages/${ticketId}`
      );
      setMessages(response.data);
    } catch (error) {
      console.error("Error fetching chat history:", error);
    }
  };

  useEffect(() => {
    const handleMessageReceived = (msg) => {
      setMessages((prev) => {
        if (!prev.find((m) => m.id === msg.id)) {
          return [...prev, msg];
        }
        return prev;
      });
    };

    fetchChatHistory(ticketId);

    const userId = sessionStorage.getItem("userId");
    setUserid(userId);
    connectWebSocket(
      ticketId,
      userId,
      handleMessageReceived,
      () => setIsEmployeeConnected(true),
      () => setIsEmployeeConnected(false)
    );

    return () => disconnectWebSocket(ticketId);
  }, [ticketId]);

  const getAvatarInitial = (senderId) => {
    return senderId.charAt(0).toUpperCase();
  };

  const getAvatarColor = (senderId) => {
    // Generate consistent color based on senderId
    const colors = [
      "#e91e63",
      "#9c27b0",
      "#673ab7",
      "#3f51b5",
      "#2196f3",
      "#03a9f4",
      "#00bcd4",
    ];
    const charSum = senderId
      .split("")
      .reduce((sum, char) => sum + char.charCodeAt(0), 0);
    return colors[charSum % colors.length];
  };

  // Format timestamp to readable format
  const formatTimestamp = (timestamp) => {
    try {
      const date = new Date(timestamp);
      if (isToday(date)) {
        return date.toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        });
      } else {
        return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        })}`;
      }
    } catch (e) {
      return timestamp;
    }
  };

  const isToday = (date) => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  // Auto-scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };
  const endChat = async () => {
    try {
      await axios.post(`${API_BASE_URL}/chatsession/end`, null, {
        params: { ticketId },
      });
      alert("Chat session ended.");
    } catch (error) {
      console.error("Error ending chat session:", error);
    }
  };
  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };
  const handleSendMessage = () => {
    if (message.trim()) {
      const newMessage = {
        senderId: userid,
        content: message,
        ticketId,
        timestamp: new Date().toISOString(), // Ensure timestamp is included
      };
      setMessages((prev) => [...prev, newMessage]); // Show the sent message in UI
      sendMessage(ticketId, userid, message);
      setMessage("");
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  return (
    <div>
      <Container
        maxWidth="xl"
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Paper
          elevation={3}
          sx={{
            width: "100%",
            maxHeight: "100vh",
            minHeight: "75vh",
            display: "flex",
            flexDirection: "column",
            padding: 2,
            textAlign: "center",
          }}
        >
          <Typography variant="h5" fontWeight="bold">
            Chat with Support
          </Typography>
          <Typography
            variant="subtitle1"
            color={isEmployeeConnected ? "green" : "gray"}
          >
            {isEmployeeConnected
              ? "Support is online"
              : "Waiting for support..."}
          </Typography>
          <Box
            sx={{
              flexGrow: 1,
              overflowY: "auto",
              maxHeight: "60vh",
              marginY: 2,
              padding: 1,
              bgcolor: "#f9f9f9",
              borderRadius: 1,
            }}
          >
            {messages.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                <Typography color="text.secondary">
                  No messages found
                </Typography>
              </Box>
            ) : (
              messages.map((msg, index) => {
                const isCurrentUser = msg.senderId === userid;
                const showSender =
                  index === 0 || messages[index - 1].senderId !== msg.senderId;
                return (
                  <>
                    <Box
                      key={msg.id}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: isCurrentUser ? "flex-end" : "flex-start",
                        mb: 1,
                        width: "100%",
                      }}
                    >
                      {showSender && (
                        <Typography variant="caption" color="text.secondary">
                          {msg.senderId}
                        </Typography>
                      )}
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-end",
                        justifyContent: isCurrentUser
                          ? "flex-end"
                          : "flex-start",
                        width: "100%", // Full width to allow right alignment
                      }}
                    >
                      {!isCurrentUser && (
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            mr: 1,
                            bgcolor: getAvatarColor(msg.senderId),
                          }}
                        >
                          {getAvatarInitial(msg.senderId)}
                        </Avatar>
                      )}
                      <Paper
                        elevation={1}
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          bgcolor: isCurrentUser ? "#1976d2" : "white",
                          color: isCurrentUser ? "white" : "black",
                        }}
                      >
                        <Typography variant="body1">{msg.content}</Typography>
                      </Paper>
                      {isCurrentUser && (
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            ml: 1,
                            bgcolor: getAvatarColor(msg.senderId),
                          }}
                        >
                          {getAvatarInitial(msg.senderId)}
                        </Avatar>
                      )}
                    </Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        mx: isCurrentUser ? 0 : 5,
                        mb: 1,
                        textAlign: isCurrentUser ? "right" : "left",
                      }}
                    >
                      {formatTimestamp(msg.timestamp)}
                    </Typography>
                  </>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </Box>
          <Divider />
          <Box sx={{ display: "flex", gap: 1, marginTop: 1 }}>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder="Type a message..."
            />

            <Button
              variant="contained"
              onClick={handleSendMessage}
              disabled={!message.trim()}
            >
              Send
            </Button>
          </Box>
        </Paper>
      </Container>
    </div>
  );
};

export default ChatComponent;
